// Alta/edición de negocio (B01 · Tests 2). El mock M6 no dibuja el form (GAP C3) → los campos se
// derivan del request-DTO `CreateRetailerRequest`. Modo edición (prop `editRetailer`) reusa el mismo
// form → PUT /retailers/{id}. Form controlado simple; los 400 del backend se muestran por toast.
import { useState } from 'react';
import { toast } from 'sonner';
import { Button, Input, Modal, PhoneInput, Select, type SelectOption } from '@/kit';
import { validateForm, type FieldErrors } from '@/lib/formValidators';
import { fromStoredPhone } from '@/lib/phoneMx';
import { RetailerTypeEnum } from '@fiado/type-kit/bin/retailOrg/enums/RetailerTypeEnum';
import type { CreateRetailerRequest } from '@fiado/type-kit/bin/retailOrg/dtos/requests/CreateRetailerRequest';
import {
    useCreateRetailer,
    useCreateSubdistributor,
    useUpdateRetailer,
    type Retailer,
} from '../api/useRetailOrg';
import { retailerCreateSchema, retailerEditSchema, type RetailerFormField } from '../lib/retailerForms';

const TYPE_OPTIONS: SelectOption[] = [
    { value: RetailerTypeEnum.CHAIN, label: 'Cadena' },
    { value: RetailerTypeEnum.SUBDISTRIBUTOR, label: 'Subdistribuidor' },
    { value: RetailerTypeEnum.FRANCHISE, label: 'Franquicia' },
];

interface Props {
    isOpen: boolean;
    onClose: () => void;
    /** Si viene, el modal EDITA ese negocio (prefill + PUT). El padre debe pasar `key` distinto por id. */
    editRetailer?: Retailer | null;
    /** Si viene (y no es edición), CREA un subdistribuidor bajo ese negocio padre (POST …/subdistributors). */
    parentId?: string;
}

const EMPTY = {
    type: RetailerTypeEnum.CHAIN as string,
    name: '',
    legalName: '',
    rfc: '',
    contactEmail: '',
    contactPhone: '',
    address1: '',
    externalNumber: '',
    neighborhood: '',
    city: '',
    state: '',
    zipCode: '',
};

export function RetailerFormModal({ isOpen, onClose, editRetailer, parentId }: Props) {
    const isEdit = !!editRetailer;
    const [form, setForm] = useState(() =>
        editRetailer
            ? {
                  type: editRetailer.type,
                  name: editRetailer.name,
                  legalName: editRetailer.legalName,
                  rfc: editRetailer.rfc,
                  contactEmail: editRetailer.contactEmail,
                  contactPhone: fromStoredPhone(editRetailer.contactPhone),
                  address1: editRetailer.address.address1 ?? '',
                  externalNumber: editRetailer.address.externalNumber ?? '',
                  neighborhood: editRetailer.address.neighborhood ?? '',
                  city: editRetailer.address.city ?? '',
                  state: editRetailer.address.state ?? '',
                  zipCode: editRetailer.address.zipCode ?? '',
              }
            : parentId
              ? { ...EMPTY, type: RetailerTypeEnum.SUBDISTRIBUTOR }
              : EMPTY,
    );
    const [errors, setErrors] = useState<FieldErrors<RetailerFormField>>({});
    const createRetailer = useCreateRetailer();
    const updateRetailer = useUpdateRetailer();
    const createSubdistributor = useCreateSubdistributor();

    // Un nodo hijo (subdistribuidor) solo puede ser SUBDISTRIBUTOR | FRANCHISE (no CHAIN, que es raíz)
    // — lo restringe CreateSubdistributorRequest. Filtramos las opciones en ese modo.
    const typeOptions = parentId
        ? TYPE_OPTIONS.filter((o) => o.value !== (RetailerTypeEnum.CHAIN as string))
        : TYPE_OPTIONS;

    const set = (key: keyof typeof EMPTY) => (value: string) => {
        setForm((prev) => ({ ...prev, [key]: value }));
        setErrors((prev) => ({ ...prev, [key]: undefined }));
    };

    const handleSubmit = () => {
        const checked = validateForm(isEdit ? retailerEditSchema : retailerCreateSchema, form);
        if (!checked.success) {
            setErrors(checked.errors);
            return;
        }
        const valid = { ...form, ...checked.data };
        const body: CreateRetailerRequest = {
            type: form.type as RetailerTypeEnum,
            name: valid.name,
            legalName: valid.legalName,
            rfc: valid.rfc,
            contactEmail: valid.contactEmail,
            contactPhone: valid.contactPhone,
            address: {
                address1: valid.address1,
                externalNumber: valid.externalNumber || undefined,
                neighborhood: valid.neighborhood || undefined,
                city: valid.city,
                state: valid.state,
                zipCode: valid.zipCode,
            },
        };
        if (editRetailer) {
            // El backend NO acepta `name` en el PUT (campo único, rename no soportado → RENAME_NOT_SUPPORTED
            // si viene presente), ni `type`/`rfc` (no están en UpdateRetailerRequest). Solo van los editables.
            updateRetailer.mutate(
                {
                    retailerId: editRetailer.retailerId,
                    body: {
                        legalName: body.legalName,
                        contactEmail: body.contactEmail,
                        contactPhone: body.contactPhone,
                        address: body.address,
                    },
                },
                {
                    onSuccess: () => {
                        toast.success('Negocio actualizado');
                        onClose();
                    },
                    onError: (error) =>
                        toast.error(error instanceof Error ? error.message : 'No se pudo actualizar'),
                },
            );
            return;
        }
        if (parentId) {
            createSubdistributor.mutate(
                { parentId, body },
                {
                    onSuccess: (created) => {
                        toast.success(`Subdistribuidor creado: ${created.name}`);
                        setForm(EMPTY);
                        onClose();
                    },
                    onError: (error) =>
                        toast.error(
                            error instanceof Error ? error.message : 'No se pudo crear el subdistribuidor',
                        ),
                },
            );
            return;
        }
        createRetailer.mutate(body, {
            onSuccess: (created) => {
                toast.success(`Negocio creado: ${created.name}`);
                setForm(EMPTY);
                onClose();
            },
            onError: (error) => {
                toast.error(error instanceof Error ? error.message : 'No se pudo crear el negocio');
            },
        });
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={isEdit ? 'Editar negocio' : parentId ? 'Nuevo subdistribuidor' : 'Nuevo negocio'}
            size="lg"
        >
            <form
                noValidate
                onSubmit={(event) => {
                    event.preventDefault();
                    handleSubmit();
                }}
                className="space-y-4"
            >
                <Select
                    label="Tipo"
                    value={form.type}
                    onChange={set('type')}
                    options={typeOptions}
                    disabled={isEdit}
                    required
                />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Input label="Nombre comercial" value={form.name} onChange={(e) => { set('name')(e.target.value); }} disabled={isEdit} hint={isEdit ? 'No editable (nombre único)' : undefined} error={errors.name} required />
                    <Input label="Razón social" value={form.legalName} onChange={(e) => { set('legalName')(e.target.value); }} error={errors.legalName} required />
                    <Input label="RFC" value={form.rfc} onChange={(e) => { set('rfc')(e.target.value.toUpperCase()); }} disabled={isEdit} hint={isEdit ? 'No editable' : 'RFC de México · 12 (moral) o 13 (física)'} maxLength={13} error={errors.rfc} required />
                    <Input label="Correo de contacto" type="email" value={form.contactEmail} onChange={(e) => { set('contactEmail')(e.target.value); }} error={errors.contactEmail} required />
                    <PhoneInput label="Teléfono de contacto" value={form.contactPhone} onValueChange={set('contactPhone')} error={errors.contactPhone} required />
                </div>

                <p className="pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Dirección
                </p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Input label="Calle" value={form.address1} onChange={(e) => { set('address1')(e.target.value); }} error={errors.address1} required />
                    <Input label="Número exterior" value={form.externalNumber} onChange={(e) => { set('externalNumber')(e.target.value); }} />
                    <Input label="Colonia" value={form.neighborhood} onChange={(e) => { set('neighborhood')(e.target.value); }} />
                    <Input label="Ciudad" value={form.city} onChange={(e) => { set('city')(e.target.value); }} error={errors.city} required />
                    <Input label="Estado" value={form.state} onChange={(e) => { set('state')(e.target.value); }} error={errors.state} required />
                    <Input label="Código postal" inputMode="numeric" maxLength={5} value={form.zipCode} onChange={(e) => { set('zipCode')(e.target.value); }} error={errors.zipCode} required />
                </div>

                <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button
                        type="submit"
                        isLoading={
                            isEdit
                                ? updateRetailer.isPending
                                : parentId
                                  ? createSubdistributor.isPending
                                  : createRetailer.isPending
                        }
                    >
                        {isEdit ? 'Guardar' : parentId ? 'Crear subdistribuidor' : 'Crear negocio'}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}
